import Globals from './Globals.js';
import Location from './Location.js';
import GroupHours from './GroupHours.js';
import Instructor from './Instructor.js';
import Tag from './Tag.js';
import rpAPI from '../service/rpAPI.js';
import { $t } from '../service/i18n.js';

class Group {

    constructor(data) {
        Object.assign(this, data);
        if (this.thumb) {
            this.image = rpAPI.getAssetUrl(`${this.thumb}.webp`, '?width=800&quality=80&format=webp');
        } else {
            this.image = null;
        }
        this.title = data?.title || data?.name || '(brak tytułu)';
        this.level = data?.level && data.level[0]?.tags_id ? new Tag(data.level[0].tags_id) : {};
        this.tags = data?.level ? data.level.map(t => new Tag(t.tags_id)) : [];
        this.condition = data?.condition ? data.condition.map(c => new Tag(c.tags_id)) : [];
        this.hasTrial = data?.has_trial || false;
        this.startFormatted = this.startF();
        this.enrollmentStart = data?.enrollment_start ? new Date(data.enrollment_start) : null;
        this.hasEarlyBirds = data?.early_birds && data?.early_birds?.length > 0 || false;
        this.earlyBirds = data?.early_birds?.map(eb => eb['related_ClassOffer_id'].id) || [];
     
        if (this.enrollmentStart && this.enrollmentStart < new Date()) {
            this.hasEarlyBirds = false;
            this.earlyBirds = [];
        }

    

        this.enrollmentStartFormatted = (this.enrollmentStart) ? this.enrollmentStart.toLocaleString('pl-PL', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        }): null;

        // Calculate year as an array of unique years between start_date and end_date (inclusive)
        if (!this.year && this.start_date && this.end_date) {
            const startYear = new Date(this.start_date).getFullYear();
            const endYear = new Date(this.end_date).getFullYear();
            this.year = `${startYear}/${endYear}`;
        }

        // Calculate months between start_date and end_date (inclusive)
        this.months = [];
        if (this.start_date && this.end_date) {
            let start = new Date(this.start_date);
            let end = new Date(this.end_date);
            start.setDate(1);
            // Set end to the first day of the month after the end date
            end = new Date(end.getFullYear(), end.getMonth() + 1, 1);

            while (start < end) {
                const monthName = $t(`months.${start.getMonth()}`);
                this.months.push(`${monthName} ${start.getFullYear()}`);
                start.setMonth(start.getMonth() + 1);
            }
        }
        this.hours = data?.class_hours?.map(ch => new GroupHours(ch?.['ClassHours_id'])) ?? [];
        // sort
        this.hours?.sort((a, b) => (new Date(a?.start_date) - new Date(b?.start_date)) || 0);


        this.sessions = this.hours.length;
        this.duration = this.hours.reduce((total, hour) => total + this.calc_hour_duration(hour.hour_end, hour.hour_start), 0);


        // Remove the 'class_hours' field from the object
        delete this.class_hours;

        this.location = data?.location ? new Location(data.location) : null;
        this.instructor = data?.instructor ? new Instructor(data.instructor) : null;
        this.free_seats = this.max_participiants - this.total_booked;
        this.lowAvailability = (this.free_seats <= Globals.APP_LOW_NUMBER_OF_PARTICIPANTS_WARNING) && (this.free_seats > 0);
        this.noAvailability = this.free_seats <= 0;
        this.available = this.free_seats > 0;
        this.hasRequirements = this.condition && this.condition.length > 0;
     




        this.requirements = this.condition?.filter(t => t.type == 'path')?.map(t => t.id) || [];
        this.requirementsFormatted = this.condition?.map(t => t.title || t).join(', ') || '';
        this.tagsFormatted = this.tags?.map(t => t.title || t).join(', ') || '';

        this.autoConfirm = this.confirm == 'auto' && this.free_seats > 0;
        this.paymentRequired = (this.confirm == 'after_payment' || this.confirm == 'after_advanced_payment') && this.free_seats > 0;
        this.ageFormatted = this.ageF();
        this.durationFormatted = this.durationF();
        this.startFormatted = this.startF();


        const sessionLabel = this.sessions === 1 ? 'spotkanie' : this.sessions >= 2 && this.sessions <= 4 ? 'spotkania' : 'spotkań';
        this.sessionsFormatted = `${this.sessions} ${sessionLabel} / ${this.duration} godzin`;

        this.agreements = data?.agreements?.map(a => ({
            id: a['Agreements_id'].id,
            title: a['Agreements_id'].title,
            required: a['Agreements_id'].required,
            condition: a['Agreements_id'].condition,
            content: a['Agreements_id'].content,
            acceptance: a['Agreements_id'].acceptance,
            document_url: a['Agreements_id'].document_url,
            sort: a['Agreements_id'].sort || 0,
        })) || [];

        // take all instructor from class_hours and merge them into one array
        this.instructors = this.instructor ? [this.instructor] : [];
        if (this.hours && this.hours.length > 0) {
            this.hours.forEach(hour => {
                if (hour.instructor && hour.instructor.id) {
                    this.instructors.push(hour.instructor);
                }
            });
        }
        // Ensure instructors list is unique
        this.instructors = this.instructors.filter((instructor, index, self) =>
            instructor && self.findIndex(i => i.id === instructor.id) === index
        );
    }


    validateRequirements(tagIds) {

        if (!this.hasRequirements) {
            return true;
        }
        if (!Array.isArray(tagIds)) return false;


        if (!tagIds || tagIds.length === 0) {
            return false;
        }
        return this.requirements.every(req => tagIds.includes(req));
    }


    calc_hour_duration(hour_end, hour_start) {
        if (!hour_end || !hour_start) {
            return 1;
        }
        const [endHours, endMinutes] = hour_end.split(':').map(Number);
        const [startHours, startMinutes] = hour_start.split(':').map(Number);

        const duration = (endHours + endMinutes / 60) - (startHours + startMinutes / 60);
        return duration > 0 ? duration : 1; // Default to 1 hour if calculation fails
    }

    async checkAvailability() {
        try {
            const response = await rpAPI.getGroupAvailability(this.id);
            if (!response) {
                throw new Error("No availability data found");
            }

            this.max_participiants = response.max_participiants;
            this.total_booked = response.total_booked;
            this.free_seats = this.max_participiants - this.total_booked;
            this.noAvailability = this.free_seats <= 0;
            this.lowAvailability = (this.free_seats <= Globals.APP_LOW_NUMBER_OF_PARTICIPANTS_WARNING) && (this.free_seats > 0);
        } catch (error) {
            console.error('Error fetching ClassOffer availability data:', error);
            throw error;
        }
    }

    hasStarted() {
        const now = new Date();
        const start = new Date(this.start_date);
        return now >= start;
    }

    startF() {
        const start = new Date(this.start_date);
        const options = { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' };

        let dateString;

        if (this.hours?.length > 0) {
            const firstHour = this.hours[0];
            //console.log("First hour:", firstHour);
            if (firstHour.start_date) {
                const firstDate = new Date(firstHour.start_date);
                dateString = firstDate.toLocaleDateString('pl-PL', options) + ', ' + firstHour.hour_start;
            }
        }

        if (!dateString) {
            dateString = start.toLocaleDateString('pl-PL', options);
        }

        return dateString.charAt(0).toUpperCase() + dateString.slice(1);
    }


    durationF() {
        const start = new Date(this.start_date);
        const end = new Date(this.end_date);
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        const startFormatted = start.toLocaleDateString('pl-PL', options);
        const endFormatted = end.toLocaleDateString('pl-PL', options);

        if (start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth()) {
            return `${start.getDate()} ${start.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })} - ${endFormatted}`;
        } else {
            if (start.getDate() === end.getDate()) {
                return startFormatted;
            }
            return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}`;
        }
    }

    ageF() {
        if (!this.age_min && !this.age_max) {
            return `wszystkie roczniki`;
        } else if (!this.age_max) {
            return `od rocznika ${this.age_min}`;
        } else if (!this.age_min) {
            return `do rocznika ${this.age_max}`;
        }
        return `roczniki ${this.age_min}-${this.age_max}`;
    }


    // dla wszystkich grup sprawdz czy uzytkownik
    // SECURITY: the `userId` param is kept only for backward-compat with
    // existing call sites; the server always derives the real user id from
    // the caller's own session token (see server.local.mjs
    // `/api/proxy/participants/validate-early-birds`) and ignores any
    // client-supplied id, so this can't be used to probe other customers.
    static validateEarlyBirds(earlyBirdIds, userId) {
        if (!earlyBirdIds || earlyBirdIds.length === 0) {
            return false;
        }
        return rpAPI.validateEarlyBirds(earlyBirdIds).catch(error => {
            console.error('Error validating early birds:', error);
            return false;
        });
    }


    static async getGroupNamesByIds(groups) {
        if (!groups || groups.length === 0) {
            return {};
        }
        const response = await rpAPI.getGroupNamesByIds(groups);

        console.log("Fetched group names for early birds:", response);

        const names = {};
        response.forEach(group => {
            names[group.id] = `${group.name} (${new Date(group.start_date).toLocaleDateString('pl-PL')})`;
        });

        return names;
    }
}

export default Group;
